import React, { useState } from 'react';

function ProductEdit({ product, setEditingProductId, authToken, categoryList, typeList, onProductSaved }) {
    const [productName, setProductName] = useState(product.productName || '');
    const [description, setDescription] = useState(product.description || '');
    const [categories, setCategories] = useState(product.categories || '');
    const [price, setPrice] = useState(product.price || '');
    const [type, setType] = useState(product.type || '');
    const [brouwer, setBrouwer] = useState(product.brouwer || '');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [saving, setSaving] = useState(false);

    const canSave = !saving && productName.trim() !== '';

    const storeProduct = async (payload) => {
        const url = process.env.REACT_APP_BASE_API_URL + 'Menu';

        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authToken,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Product opslaan mislukt.');
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            await response.json();
        }
    };

    const saveProduct = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');
        setSaving(true);

        const updatedProduct = {
            ...product,
            productName,
            description,
            categories,
            price,
            type,
            brouwer,
        };

        try {
            await storeProduct(updatedProduct);

            if (onProductSaved) {
                onProductSaved(updatedProduct);
            }

            setSuccessMessage('Product succesvol opgeslagen.');
            setEditingProductId(null);
        } catch (error) {
            setErrorMessage(error.message || 'Er ging iets fout tijdens opslaan.');
        } finally {
            setSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingProductId(null);
    };

    return (
        <div className="page-wrap">
            <section className="admin-card anim-fade-in">
                <div className="header-row">
                    <div>
                        <h2 className="headline">Product {product.productNumber} bewerken</h2>
                        <p className="subtle-text">Werk de productgegevens bij en sla veilig op.</p>
                    </div>
                    <button type="button" className="btn-muted" onClick={cancelEdit} disabled={saving}>
                        Terug naar lijst
                    </button>
                </div>

                {errorMessage && <div className="error-banner" role="alert">{errorMessage}</div>}
                {successMessage && <div className="ok-banner" role="status" aria-live="polite">{successMessage}</div>}

                <form className="form-grid" onSubmit={saveProduct}>
                    <div className="form-grid cols-2">
                        <div className="field-group">
                            <label htmlFor="productName">Product Name</label>
                            <input
                                id="productName"
                                type="text"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="field-group">
                            <label htmlFor="price">Price</label>
                            <input
                                id="price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="field-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                        />
                    </div>

                    <div className="form-grid cols-2">
                        <div className="field-group">
                            <label htmlFor="categories">Categories</label>
                            <input
                                id="categories"
                                type="text"
                                value={categories}
                                onChange={(e) => setCategories(e.target.value)}
                            />
                            <p className="helper-note">Comma separated lijst. Bijvoorbeeld: Van het vat, Uitgelicht</p>
                            <div className="list-block">Bekende categorieen: {categoryList.length ? categoryList.join(' | ') : '-'}</div>
                        </div>
                        <div className="field-group">
                            <label htmlFor="type">Type</label>
                            <input
                                id="type"
                                type="text"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            />
                            <p className="helper-note">1 type per product, meestal bierstijl.</p>
                            <div className="list-block">Bekende types: {typeList.length ? typeList.join(' | ') : '-'}</div>
                        </div>
                    </div>

                    <div className="field-group">
                        <label htmlFor="brouwer">Brewery</label>
                        <input
                            id="brouwer"
                            type="text"
                            value={brouwer}
                            onChange={(e) => setBrouwer(e.target.value)}
                        />
                    </div>

                    <div className="row-actions">
                        <button type="submit" className="btn-main" disabled={!canSave}>
                            {saving ? 'Saving...' : 'Save product'}
                        </button>
                        <button type="button" className="btn-muted" onClick={cancelEdit} disabled={saving}>
                            Cancel
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

export default ProductEdit;
